import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      await axios.post(`${API}/cart/add?product_id=${product.id}&quantity=${quantity}`, {}, {
        withCredentials: true // Enable cookies for guest session
      });
      toast.success('Producto agregado al carrito');
      fetchCartCount();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar al carrito');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-purple-400 text-xl">Cargando...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400 text-xl">Producto no encontrado</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="product-detail-page">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/shop')}
          className="mb-8 text-purple-400 hover:text-purple-300"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" /> Volver a la Tienda
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="glass rounded-2xl p-8 fade-in">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-96 object-cover rounded-xl"
              data-testid="product-image"
            />
          </div>

          {/* Product Info */}
          <div className="fade-in">
            <div className="flex items-center gap-2 mb-4">
              <div className="inline-block px-4 py-1 bg-purple-600/20 border border-purple-600/50 rounded-full text-purple-400 text-sm" data-testid="product-category">
                {product.category}
              </div>
              {product.discount_percentage > 0 && (
                <div className="inline-block px-4 py-1 bg-red-600 rounded-full text-white text-sm font-bold animate-pulse">
                  -{product.discount_percentage}% OFF
                </div>
              )}
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="product-name">{product.name}</h1>
            
            <p className="text-gray-400 text-lg mb-6" data-testid="product-description">{product.description}</p>
            
            <div className="flex items-center gap-4 mb-6">
              <div data-testid="product-price">
                {product.discount_percentage > 0 ? (
                  <div>
                    <div className="text-gray-500 line-through text-xl">{formatCurrency(product.price)}</div>
                    <div className="text-4xl font-bold text-purple-400">
                      {formatCurrency(product.price * (1 - product.discount_percentage / 100))}
                    </div>
                    <div className="text-green-400 text-sm font-bold mt-1">
                      ¡Ahorra {formatCurrency(product.price - product.price * (1 - product.discount_percentage / 100))}!
                    </div>
                  </div>
                ) : (
                  <div className="text-4xl font-bold text-purple-400">
                    {formatCurrency(product.price)}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2 text-gray-400">
                <Package size={20} />
                <span data-testid="product-stock">
                  {product.stock > 0 ? `${product.stock} en stock` : 'Sin stock'}
                </span>
              </div>
            </div>

            {product.stock > 0 && (
              <div className="glass rounded-xl p-6 mb-6">
                <label className="block text-gray-300 mb-3">Cantidad</label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    data-testid="decrease-quantity"
                  >
                    -
                  </Button>
                  <span className="text-2xl font-bold text-white w-16 text-center" data-testid="quantity-display">{quantity}</span>
                  <Button
                    variant="outline"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    data-testid="increase-quantity"
                  >
                    +
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={addToCart}
              disabled={product.stock === 0}
              size="lg"
              className="w-full btn-primary bg-purple-600 hover:bg-purple-700 text-white text-lg py-6 rounded-full"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="mr-2" />
              {product.stock > 0 ? 'Agregar al Carrito' : 'Sin Stock'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;